import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type CreateReportCardLineData,
  type CreateReportPeriodData,
  type EntryScope,
  type ReportPeriod,
  type SaveMarkData,
  type UpdateReportCardLineData,
  type UpdateReportPeriodData,
  reportCardsApi,
} from './report-cards';

export const reportCardsKeys = {
  all: ['report-cards'] as const,
  periods: (schoolYearId: string) => ['report-cards', 'periods', schoolYearId] as const,
  lines: (periodId: string, gradeLevel: number) => ['report-cards', 'lines', periodId, gradeLevel] as const,
  grids: (periodId: string) => ['report-cards', 'grids', periodId] as const,
  scopedGrids: (periodId: string, scope: EntryScope) => ['report-cards', 'grids', periodId, scope] as const,
  // Under `grids` so every mark save that refreshes the grids refreshes the switch's counts too.
  entrySummary: (periodId: string) => ['report-cards', 'grids', periodId, 'summary'] as const,
  openPeriod: ['report-cards', 'open-period'] as const,
  completeness: (periodId: string) => ['report-cards', 'completeness', periodId] as const,
  periodCards: (periodId: string) => ['report-cards', 'period-cards', periodId] as const,
  periodViews: (periodId: string) => ['report-cards', 'period-views', periodId] as const,
  scales: ['report-cards', 'scales'] as const,
  studentCards: (studentId: string) => ['report-cards', 'student-cards', studentId] as const,
  cardView: (cardId: string) => ['report-cards', 'card-view', cardId] as const,
};

export function useReportPeriodsQuery(schoolYearId: string | undefined) {
  return useQuery({
    queryKey: reportCardsKeys.periods(schoolYearId ?? ''),
    queryFn: () => reportCardsApi.listPeriods(schoolYearId!),
    enabled: !!schoolYearId,
  });
}

export function useReportCardLinesQuery(periodId: string | undefined, gradeLevel: number | undefined) {
  return useQuery({
    queryKey: reportCardsKeys.lines(periodId ?? '', gradeLevel ?? 0),
    queryFn: () => reportCardsApi.listLines(periodId!, gradeLevel!),
    enabled: !!periodId && gradeLevel !== undefined,
  });
}

/** Waits while `scope` is undefined, so the office never loads one view only to swap to the other. */
export function useMyEntryGridsQuery(periodId: string | undefined, scope: EntryScope | undefined) {
  return useQuery({
    queryKey: reportCardsKeys.scopedGrids(periodId ?? '', scope ?? 'mine'),
    queryFn: () => reportCardsApi.myGrids(periodId!, scope!),
    enabled: !!periodId && !!scope,
  });
}

export function useMyEntrySummaryQuery(periodId: string | undefined) {
  return useQuery({
    queryKey: reportCardsKeys.entrySummary(periodId ?? ''),
    queryFn: () => reportCardsApi.myEntrySummary(periodId!),
    enabled: !!periodId,
  });
}

/** Teacher-safe: unlike `useReportPeriodsQuery`, this hits an endpoint any teacher can call. */
export function useMyOpenPeriodQuery() {
  return useQuery({
    queryKey: reportCardsKeys.openPeriod,
    queryFn: () => reportCardsApi.myOpenPeriod(),
  });
}

export function useCompletenessQuery(periodId: string | undefined) {
  return useQuery({
    queryKey: reportCardsKeys.completeness(periodId ?? ''),
    queryFn: () => reportCardsApi.completeness(periodId!),
    enabled: !!periodId,
  });
}

export function useGradingScalesQuery() {
  return useQuery({
    queryKey: reportCardsKeys.scales,
    queryFn: () => reportCardsApi.listScales(),
  });
}

/** A teacher's own read of the report cards of the students they teach; `[]` when they teach none. */
export function useStudentReportCardsQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: reportCardsKeys.studentCards(studentId ?? ''),
    queryFn: () => reportCardsApi.studentCards(studentId!),
    enabled: !!studentId,
  });
}

export function useReportCardViewQuery(cardId: string | undefined) {
  return useQuery({
    queryKey: reportCardsKeys.cardView(cardId ?? ''),
    queryFn: () => reportCardsApi.cardView(cardId!),
    enabled: !!cardId,
  });
}

/** "Report" prefix disambiguates from `useCreatePeriodMutation` in `periods/hooks/use-periods.ts`,
 *  which manages timetable periods — a different concept re-exported from the same barrel file. */
export function useCreateReportPeriodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateReportPeriodData) => reportCardsApi.createPeriod(dto),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.periods(data.schoolYearId) });
    },
  });
}

export function useUpdateReportPeriodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateReportPeriodData }) => reportCardsApi.updatePeriod(id, dto),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.periods(data.schoolYearId) });
    },
  });
}

export function useOpenPeriodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => reportCardsApi.openPeriod(periodId),
    // Opening materialises the whole subject matrix and every student's card, so a narrower
    // invalidation would still leave the lines, grids and completeness board of this period stale.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.all });
    },
  });
}

export function useClosePeriodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => reportCardsApi.closePeriod(periodId),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.periods(data.schoolYearId) });
    },
  });
}

/** Publishing and unpublishing change a period's status, so the year's period list refetches. */
export function usePublishPeriodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, force }: { periodId: string; schoolYearId: string; force: boolean }) =>
      reportCardsApi.publishPeriod(periodId, force),
    onSuccess: (_data, { schoolYearId }) =>
      queryClient.invalidateQueries({ queryKey: reportCardsKeys.periods(schoolYearId) }),
  });
}

export function useUnpublishPeriodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId }: { periodId: string; schoolYearId: string }) => reportCardsApi.unpublishPeriod(periodId),
    onSuccess: (_data, { schoolYearId }) =>
      queryClient.invalidateQueries({ queryKey: reportCardsKeys.periods(schoolYearId) }),
  });
}

export function useSetCommentOnlyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, cardId, commentOnly }: { periodId: string; cardId: string; commentOnly: boolean }) =>
      reportCardsApi.setCommentOnly(periodId, cardId, commentOnly),
    onSuccess: (_data, { periodId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: reportCardsKeys.periodCards(periodId) }),
        queryClient.invalidateQueries({ queryKey: reportCardsKeys.completeness(periodId) }),
        queryClient.invalidateQueries({ queryKey: reportCardsKeys.grids(periodId) }),
      ]),
  });
}

export function useNotifyPeriodMutation() {
  return useMutation({ mutationFn: (periodId: string) => reportCardsApi.notifyPeriod(periodId) });
}

export function useRemindPeriodMutation() {
  return useMutation({ mutationFn: (periodId: string) => reportCardsApi.remindPeriod(periodId) });
}

export function useCreateLineMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, dto }: { periodId: string; dto: CreateReportCardLineData }) =>
      reportCardsApi.createLine(periodId, dto),
    onSuccess: (data, { periodId }) => {
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.lines(periodId, data.gradeLevel) });
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.completeness(periodId) });
    },
  });
}

export function useUpdateLineMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, lineId, dto }: { periodId: string; lineId: string; dto: UpdateReportCardLineData }) =>
      reportCardsApi.updateLine(periodId, lineId, dto),
    onSuccess: (data, { periodId }) => {
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.lines(periodId, data.gradeLevel) });
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.grids(periodId) });
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.completeness(periodId) });
    },
  });
}

export function useSetLineTeachersMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, lineId, teacherIds }: { periodId: string; lineId: string; teacherIds: string[] }) =>
      reportCardsApi.setLineTeachers(periodId, lineId, teacherIds),
    onSuccess: (data, { periodId }) => {
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.lines(periodId, data.gradeLevel) });
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.completeness(periodId) });
    },
  });
}

export function useReorderLinesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, gradeLevel, lineIds }: { periodId: string; gradeLevel: number; lineIds: string[] }) =>
      reportCardsApi.reorderLines(periodId, gradeLevel, lineIds),
    onSuccess: (_data, { periodId, gradeLevel }) => {
      void queryClient.invalidateQueries({ queryKey: reportCardsKeys.lines(periodId, gradeLevel) });
    },
  });
}

/**
 * The outstanding badge and the completeness board are both derived from marks — a save that
 * only invalidated the grid would leave either one showing a stale count until something else
 * happened to refetch it.
 */
function useInvalidateMarkEffects() {
  const queryClient = useQueryClient();
  return (periodId: string) => {
    void queryClient.invalidateQueries({ queryKey: reportCardsKeys.grids(periodId) });
    void queryClient.invalidateQueries({ queryKey: reportCardsKeys.completeness(periodId) });
  };
}

export type SaveMarkVariables = SaveMarkData & { cardId: string; lineId: string; periodId: string };

export function useSaveMarkMutation() {
  const invalidate = useInvalidateMarkEffects();
  return useMutation({
    // `valueId`/`comment` are picked individually rather than spread from `variables` so an
    // absent field stays absent in the request body instead of round-tripping as `undefined`.
    mutationFn: ({ cardId, lineId, valueId, comment }: SaveMarkVariables) =>
      reportCardsApi.saveMark(cardId, lineId, { valueId, comment }),
    onSuccess: (_data, { periodId }) => invalidate(periodId),
  });
}

/** Who has opened a published period's cards; nothing to ask for before it is published. */
export function usePeriodViewsQuery(period: ReportPeriod) {
  return useQuery({
    queryKey: reportCardsKeys.periodViews(period.id),
    queryFn: () => reportCardsApi.periodViews(period.id),
    enabled: period.status === 'published',
  });
}
